/**
 * Playing one lesson.
 *
 * The loop, and only the loop: ask the server for a lesson, show its exercises
 * one at a time, buffer what the child answered, and close the lesson at the
 * end. The lesson's *shape* was decided by the builder before any of this ran
 * ([D-050](../../../docs/DECISIONS.md#d-050)), so nothing here reorders,
 * substitutes or drops an exercise — a client that quietly skipped one would
 * make the difficulty engine's evidence a lie.
 *
 * The economy — points, acorns, streaks, the combo meter in the mockups — is
 * deliberately not here yet ([Roadmap](../../../docs/ROADMAP.md)). What is here
 * is the part everything else hangs off.
 */

import '@capytalk/tokens/blocks/feedback.css';
// `.combo` — the flame pill — is the hard-mode block's, and this is the screen
// hard mode happens on.
import '@capytalk/tokens/blocks/hardmode.css';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type Client,
  type Finishing,
  type Playing,
  type Queued,
  type Trouble,
  today,
  troubleFrom,
} from './api.js';
import * as effects from './effects.js';
import {
  NO_COMBO,
  type Outcome,
  afterAnswer,
  retriable,
  retryLater,
} from '@capytalk/core';
import { canSpeak, say } from './audio.js';
import { CLOSE, CORRECT, FINISHED_GENTLY, FINISHED_WELL, pick } from './praise.js';
import { type SpeechRecognizer, canListenPrivately, recognizer } from './speech.js';
import Boundary from './Boundary.js';
import BreakPrompt from './BreakPrompt.js';
import RecapScreen from './Recap.js';
import { reportAbandon } from './telemetry.js';
import { activePrefs } from './prefs.js';
import { ExerciseView, type Verdict } from './exercises.js';
import Sneakers from './Sneakers.js';
import { useExerciseOnScreen } from './Hint.js';
import {
  ReportButton,
  ReportSheet,
  THANKS,
  reportedVerdict,
  sendReport,
  useReporting,
} from './Report.js';
import { Outbox, type WireAttempt } from './outbox.js';

/**
 * What this build can actually do, asked fresh each time.
 *
 * A statement about the *app and this device*, never a claim: `audio` is true
 * only when a word can really be made audible here — a recording the course
 * has, or an English voice the platform has
 * ([`canSpeak`](./audio.ts), [D-064](../../../docs/DECISIONS.md#d-064)). On a
 * device with neither, it stays false and the builder substitutes the fallbacks
 * their authors wrote, exactly as before.
 *
 * `mic` says whether this device can listen **without the audio leaving it**
 * ([`speech.ts`](./speech.ts), [D-004](../../../docs/DECISIONS.md#d-004)) — not
 * whether it has a microphone. On most browsers today the answer is still no,
 * and every speaking exercise arrives as `shadow`: listen, repeat if you like,
 * full credit ([Accessibility](../../../docs/ACCESSIBILITY.md)).
 *
 * Asked once and awaited, because the answer involves the browser checking for
 * an on-device model. Asked *before* the lesson is built, because it changes
 * which exercises the builder picks.
 */
export const capabilities = async (): Promise<{ mic: boolean; audio: boolean }> => ({
  // A child who switched the microphone off gets the no-mic version of every
  // speaking exercise, exactly as a device without one does (D-133).
  mic: activePrefs().useMic && (await canListenPrivately()),
  audio: canSpeak(),
});

type Phase =
  | { readonly at: 'loading' }
  | { readonly at: 'trouble'; readonly trouble: Trouble }
  /** Sneakers, before the first question. Only when the author wrote him one. */
  | { readonly at: 'intro' }
  | { readonly at: 'asking' }
  | {
      readonly at: 'feedback';
      readonly verdict: Extract<Verdict, { kind: 'answered' }>;
      /** A miss that will be asked again later in this lesson (D-162). */
      readonly again: boolean;
    }
  | { readonly at: 'done'; readonly score: number; readonly earned: Finishing | null };

/**
 * What Sneakers says, read from a response that might not have it.
 *
 * The types say `narration` is always there. So did the types about `payout`,
 * right up until a server one deploy behind sent a response without it and a
 * child mid-lesson got a blank screen ([D-066](../../../docs/DECISIONS.md#d-066)).
 * **The server can be older than the client** — that is every rolling deploy —
 * and a missing mascot line is the cheapest possible thing to be wrong about.
 * So this reads through optionally and the lesson simply starts quietly.
 */
const saidBy = (
  lesson: Playing | null,
  when: 'intro' | 'outro',
): Playing['narration']['intro'] => lesson?.narration?.[when] ?? null;

const idFor = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  // happy-dom and older Safari. Uniqueness is all this needs: the id exists so
  // a replayed batch is one row, and it never leaves this device's own lesson.
  `id-${String(Math.random()).slice(2)}-${String(Date.now())}`;

/**
 * "Fancy a quick one?"
 *
 * Shows nothing at all when there is no offer, which is almost always. The two
 * buttons are the same size and the same shape — an offer where "no" is smaller
 * or greyer than "yes" is not optional, it is pressure.
 */
function BonusOffer({
  offer,
  onPlay,
  onDecline,
}: {
  readonly offer:
    | { readonly badge: string; readonly nameL1: string; readonly offerL1: string }
    | null
    | undefined;
  readonly onPlay: ((day: string) => void) | undefined;
  readonly onDecline: () => void;
}) {
  // No offer, or a runner with nowhere to send one. Either way, say nothing:
  // a child who is not offered a round should see no trace of the idea.
  if (offer === null || offer === undefined || onPlay === undefined) return null;

  return (
    <div
      className="card"
      style={{ marginTop: 16, width: '100%', textAlign: 'left', background: '#FDF6E0' }}
    >
      <div style={{ fontWeight: 800 }}>
        {offer.badge} {offer.nameL1}
      </div>
      <p className="tiny" style={{ marginTop: 4 }}>
        {offer.offerL1}
      </p>
      <div className="row" style={{ gap: 8, marginTop: 10 }}>
        <button
          type="button"
          className="btn"
          style={{ minHeight: 48 }}
          onClick={() => {
            onPlay(today());
          }}
        >
          Yes please!
        </button>
        <button
          type="button"
          className="btn ghost"
          style={{ minHeight: 48 }}
          onClick={onDecline}
        >
          Not today
        </button>
      </div>
    </div>
  );
}

export default function Lesson({
  client,
  lessonId,
  replayLevel,
  onBonus,
  onLeave,
  onNextChapter,
}: {
  readonly client: Client;
  /**
   * The lesson being played, or **null** for a practice session.
   *
   * Null is not "unknown": there is no lesson. The mistakes pool is a session of
   * its own, with its own start, its own write path and its own payout
   * ([D-074](../../../docs/DECISIONS.md#d-074)) — but the *runner* is the same
   * one, because for a child the two are the same thing: questions, one at a
   * time, and a recap at the end.
   */
  readonly lessonId: string | null;
  /**
   * Which pass over the chapter, if this is a replay
   * ([D-109](../../../docs/DECISIONS.md#d-109)).
   *
   * Sent with both the start and the finish, and clamped server-side in both
   * places — the client asks, it does not decide. Undefined for the ordinary
   * lesson and for practice, which is not a pass over anything.
   */
  readonly replayLevel?: number | undefined;
  /**
   * Take up a bonus round ([D-024](../../../docs/DECISIONS.md#d-024)).
   *
   * Optional, because practice sessions never offer one and the mistakes
   * runner mounts this same component.
   */
  readonly onBonus?: ((day: string) => void) | undefined;
  /** Back to the path, which redraws itself on the way. */
  readonly onLeave: () => void;
  /**
   * Back to the path, opened on this chapter: *Keep going* after a lesson that
   * closed a chapter ([D-183](../../../docs/DECISIONS.md#d-183)).
   */
  readonly onNextChapter?: ((chapterId: string) => void) | undefined;
}) {
  const [lesson, setLesson] = useState<Playing | null>(null);
  const [phase, setPhase] = useState<Phase>({ at: 'loading' });
  const [index, setIndex] = useState(0);
  /**
   * The questions, in order. State rather than the lesson's own list, because
   * a missed one goes back in a few places later (D-162).
   */
  const [queue, setQueue] = useState<readonly Queued[]>([]);
  /** Exercises already given their second chance: once each, never a loop. */
  const retried = useRef(new Set<string>());
  /**
   * How far the bar has got. Only ever moves forward: a question going back in
   * makes the lesson longer, never the child's progress shorter.
   */
  const [furthest, setFurthest] = useState(0);

  const outbox = useRef(new Outbox());
  /**
   * What the device could do when the lesson started.
   *
   * Kept so the finish can report it without asking again: `capabilities()`
   * touches the microphone permission, and asking a second time at the end of
   * a lesson is a prompt a child did not ask for.
   */
  const could = useRef({ mic: false, audio: true });
  /**
   * The run of right answers, for the flame in the top bar.
   *
   * A display, not a source of truth. What a lesson is worth is decided by the
   * server from the attempts it stored ([`earning.ts`](../../server/src/earning.ts)),
   * so this counter being wrong would look wrong and pay right.
   */
  const [combo, setCombo] = useState(NO_COMBO);
  const reporting = useReporting();
  const runId = useRef(idFor());
  const shownAt = useRef(Date.now());
  /** When this sitting began, for how long they lasted if they give up. */
  const startedAt = useRef(Date.now());
  // Every score this run produced, for the lesson's own score at the end.
  const scores = useRef<number[]>([]);
  /**
   * Whether the closing call is already on its way.
   *
   * A child who taps the last button twice — and they do, on a slow connection,
   * because nothing has visibly happened yet — used to send `lesson.finish`
   * twice. The server is idempotent about that now
   * ([D-083](../../../docs/DECISIONS.md#d-083)) and the second call is a
   * correct no-op, but the right number of requests is still one: this is the
   * end of a lesson, which is exactly when a connection is worst and when
   * getting the payout screen up matters most.
   *
   * A ref rather than state because it must take effect on the *next line*, not
   * on the next render — two taps in one frame both read the old state.
   */
  const finishing = useRef(false);

  /**
   * Leaving.
   *
   * A child who walks out before the ending has **abandoned** the lesson, and
   * where they got to is the single most valuable content signal there is
   * ([Telemetry](../../../docs/TELEMETRY.md#the-two-new-events)) — one glance at
   * it says lesson 3 of chapter 14 is broken.
   *
   * Recorded before leaving and never awaited: the child is already gone, and a
   * telemetry write must not be the thing standing between them and the way out.
   * A practice session has no lesson to abandon, so there is nothing to say.
   */
  const leave = useCallback(() => {
    if (lessonId !== null && phase.at !== 'done') {
      reportAbandon({
        lessonId,
        sessionId: runId.current,
        exerciseOrdinal: index,
        elapsedMs: Date.now() - startedAt.current,
      });
    }
    onLeave();
  }, [lessonId, phase.at, index, onLeave]);

  const send = useCallback(
    (batch: readonly WireAttempt[]) =>
      client.attempt.record.mutate({ lessonId, attempts: [...batch] }),
    [client, lessonId],
  );

  /**
   * One way to listen for the whole lesson.
   *
   * Built once rather than per question: a child should meet the microphone
   * permission once at most, and a recogniser left running behind the next
   * screen is a light they can see. `null` on every device that cannot keep the
   * audio at home, which is most of them today
   * ([D-004](../../../docs/DECISIONS.md#d-004)).
   */
  const [listener, setListener] = useState<SpeechRecognizer | null>(null);

  useEffect(() => {
    let live = true;
    void capabilities().then((can) => {
      if (live && can.mic) setListener(recognizer());
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    // Written out rather than chained: the two `start` routes answer with
    // different shapes, and a ternary inside a `.then` leaves TypeScript to
    // pick between them before it knows which one ran.
    void (async () => {
      try {
        const can = await capabilities();
        could.current = can;
        const started =
          lessonId === null
            ? await client.practice.start.mutate({ capabilities: can })
            : await client.lesson.start.mutate({
                lessonId,
                capabilities: can,
                replayLevel,
              });
        if (!live) return;
        setLesson(started);
        setQueue(started.exercises);
        // The clock on the first question starts when the question does, not
        // while Sneakers is still talking — otherwise his line is counted as
        // the child being slow.
        setPhase(
          saidBy(started, 'intro') === null ? { at: 'asking' } : { at: 'intro' },
        );
        shownAt.current = Date.now();
      } catch (error: unknown) {
        if (live) setPhase({ at: 'trouble', trouble: troubleFrom(error) });
      }
    })();
    return () => {
      live = false;
    };
  }, [client, lessonId, replayLevel]);

  const exercises = queue;
  const current = exercises[index];
  useExerciseOnScreen(current?.exerciseId ?? null);

  const finish = useCallback(async () => {
    if (finishing.current) return;
    finishing.current = true;

    // Everything that has not landed yet, once more, before the lesson closes.
    // It is the same batch with the same ids, so a retry after a token expired
    // mid-lesson costs nothing (D-059).
    await outbox.current.flush(send);

    const marks = scores.current;
    // The mean of what was actually assessed. An exercise this build could show
    // but not ask is not in here at all, so it neither helps nor hurts.
    const score =
      marks.length === 0 ? 0 : marks.reduce((sum, s) => sum + s, 0) / marks.length;

    let earned: Finishing | null = null;
    try {
      // `day` is this device's local date, because a streak is counted in the
      // child's own timezone and only the device knows which one that is.
      earned =
        lessonId === null
          ? await client.practice.finish.mutate({
              sessionId: runId.current,
              day: today(),
            })
          : await client.lesson.finish.mutate({
              lessonId,
              score,
              day: today(),
              // The same id already stamped on every answer. The recap uses it
              // to work out which words this sitting was the first to teach.
              sessionId: runId.current,
              replayLevel,
              // So the server only offers a bonus round this device can play.
              // What it could do when the lesson started, not asked again:
              // a permission prompt at the end of a lesson is a prompt nobody
              // asked for.
              capabilities: { audio: could.current.audio },
            });
    } catch {
      // The answers are stored either way; only the closing is lost, and the
      // lesson can be finished again. Better than trapping a child on a screen.
      //
      // So the guard comes off: it is there to stop a *duplicate* call, never to
      // turn one failed call into a lesson that can never be closed.
      finishing.current = false;
    }
    setPhase({ at: 'done', score, earned });
  }, [client, lessonId, replayLevel, send]);

  const next = useCallback(() => {
    if (exercises.length > 0) {
      setFurthest((was) => Math.max(was, (index + 1) / exercises.length));
    }
    if (index + 1 < exercises.length) {
      setIndex(index + 1);
      setPhase({ at: 'asking' });
      shownAt.current = Date.now();
      return;
    }
    void finish();
  }, [index, exercises.length, finish]);

  /** Buffer one answer for sending, and count it toward the lesson's score. */
  const record = useCallback(
    (
      exerciseId: string,
      outcome: Outcome,
      score: number,
      phonemes?: Readonly<Record<string, number>>,
    ) => {
      setCombo((was) => {
        const now = afterAnswer(was, outcome);
        /*
          The feedback cue ([Sound](../../../docs/SOUND.md)). Fired here rather
          than in the widgets, because this is the one place that knows the
          *run* — and the correct sound rises with it, so five in a row sounds
          different from one.

          Computed from the new combo rather than the old: the child has just
          earned this answer, and hearing the sound for the run they were on
          before it would be a beat behind them.
        */
        if (outcome === 'correct') effects.correct(now.run, now.hard);
        else effects.play('not-yet');
        return now;
      });
      scores.current.push(score);
      outbox.current.add({
        attemptId: idFor(),
        exerciseId,
        sessionId: runId.current,
        outcome,
        score,
        latencyMs: Math.min(600_000, Date.now() - shownAt.current),
        hintsUsed: 0,
        // Derived numbers only. The audio never left the device and was never
        // recorded ([D-011](../../../docs/DECISIONS.md#d-011)); what travels is
        // a confidence per sound, and only when something was actually heard.
        phonemeScores: phonemes ?? null,
        at: new Date().toISOString(),
      });
      // Not awaited: a child does not wait for the network between questions,
      // and anything still unsent goes out again when the lesson closes.
      void outbox.current.flush(send);
    },
    [send],
  );

  const answered = useCallback(
    (verdict: Verdict) => {
      if (current === undefined) return;
      // One answer per question. The exercise stays on screen under the
      // feedback sheet, and its buttons stay tappable — "I said it" pressed
      // again, or a double tap on a tile, used to record a second attempt and
      // push the combo and the rewards up each time. A browser test clicking
      // through a lesson found the combo at 276.
      if (phase.at !== 'asking') return;

      if (verdict.kind === 'seen') {
        // Nothing was asked, so there is nothing to say about it and nothing to
        // record. A feedback panel here would be a second tap for a child who
        // has already tapped "Next" on the card itself.
        next();
        return;
      }

      record(current.exerciseId, verdict.outcome, verdict.score, verdict.phonemes);
      // A miss comes back a few questions later, once (D-162). Only a clear
      // miss: "partial" was nearly there and a skip was not an attempt.
      const again =
        verdict.outcome === 'incorrect' &&
        retriable(current.type) &&
        !retried.current.has(current.exerciseId);
      if (again) {
        retried.current.add(current.exerciseId);
        setQueue((was) => retryLater(was, index));
      }
      setPhase({ at: 'feedback', verdict, again });
    },
    [current, next, record, phase.at, index],
  );

  /**
   * A child said this question is broken.
   *
   * The answer is marked correct whatever they had chosen, and the lesson moves
   * **straight on** — no feedback panel. Telling a child "Yes! That is right"
   * about a question they just told us is broken would be absurd, and D-044 is
   * explicit that Sneakers thanks them and moves on.
   */
  const reported = useCallback(() => {
    if (current === undefined) return;
    const { outcome, score } = reportedVerdict();
    record(current.exerciseId, outcome, score);
    next();
  }, [current, next, record]);

  if (phase.at === 'loading') {
    return (
      <Frame progress={0} onLeave={leave}>
        <div className="grow pad center">
          <p className="sub">Getting the lesson…</p>
        </div>
      </Frame>
    );
  }

  if (phase.at === 'trouble') {
    return (
      <Frame progress={0} onLeave={leave}>
        <div className="grow pad center">
          <p className="sub" role="alert">
            {phase.trouble.kind === 'offline'
              ? 'No connection. Is the wi-fi on?'
              : 'The lesson would not load.'}
          </p>
          <button className="btn ghost" type="button" onClick={onLeave}>
            Back
          </button>
        </div>
      </Frame>
    );
  }

  const opening = saidBy(lesson, 'intro');
  if (phase.at === 'intro' && opening !== null) {
    const line = opening;
    return (
      <Frame progress={0} onLeave={leave}>
        <div className="grow pad center">
          {/*
            `.sn-row` is Sneakers' own; wrapping him in a second one nests two
            flex rows and squeezes the bubble. The width and the left-aligned
            text are this screen's — the column around him is centred, and a
            sentence centred inside a speech bubble reads like a poster.
          */}
          <div style={{ width: '100%', textAlign: 'left' }}>
            <Sneakers text={line.text} audioRef={line.audioRef} size={72} />
          </div>
          <button
            className="btn"
            type="button"
            style={{ marginTop: 22 }}
            onClick={() => {
              setPhase({ at: 'asking' });
              shownAt.current = Date.now();
            }}
          >
            Let’s go
          </button>
        </div>
      </Frame>
    );
  }

  if (phase.at === 'done') {
    return (
      <div className="frame">
        <div className="grow center pad">
          <div style={{ fontSize: 32, letterSpacing: 8 }}>✨🎉✨</div>
          <h1 style={{ marginTop: 2 }}>You did it!</h1>
          <p className="sub" style={{ marginTop: 4 }}>
            {lesson?.titleL1 ?? ''}
          </p>
          {/*
            `.bubble` carries `flex:1` because the mockups always put it in a
            `.sn-row` beside Sneakers, where that means "fill the rest of the
            line". Dropped straight into a column it means "fill the rest of the
            screen" instead, so the row goes with it.
          */}
          <div style={{ marginTop: 16, width: '100%', textAlign: 'left' }}>
            <Farewell phase={phase} outro={saidBy(lesson, 'outro')} />
          </div>
          {/*
            Boxed, because a server one deploy behind sends a shape the types
            say is impossible — and a recap that will not render must not take
            the whole finish screen with it, or a child is trapped here. The
            fallback is the one button that matters.
          */}
          {/*
            The bonus round, offered before the recap because it is the thing
            that is *about to happen* and the recap is the thing that already
            did ([D-024](../../../docs/DECISIONS.md#d-024)).

            Declining is a plain button of the same size as accepting, worded
            without a flinch: nothing here should make a tired child feel they
            are letting anyone down by going to bed.
          */}
          <BonusOffer
            // `Finishing` is a lesson *or* a practice session, and practice is
            // not a lesson that can earn a bonus round — so this narrows rather
            // than reaching for a field the union does not have.
            offer={
              phase.earned !== null && 'bonus' in phase.earned
                ? phase.earned.bonus
                : null
            }
            onPlay={onBonus}
            onDecline={onLeave}
          />
          <Boundary
            what="the recap"
            fallback={
              <button
                className="btn"
                type="button"
                style={{ marginTop: 18 }}
                onClick={onLeave}
              >
                Stop for today
              </button>
            }
          >
            <RecapScreen
              earned={phase.earned}
              onLeave={onLeave}
              onNextChapter={onNextChapter}
              client={client}
            />
          </Boundary>
        </div>
        {/*
          Over the finish screen, after about 25 minutes (D-160). Boxed like the
          recap: a prompt that will not render must never cost the child the
          screen underneath, so its fallback is nothing at all.
        */}
        <Boundary what="the break prompt" fallback={null}>
          <BreakPrompt client={client} onBreak={onLeave} />
        </Boundary>
      </div>
    );
  }

  const progress = exercises.length === 0 ? 1 : furthest;

  return (
    <Frame
      progress={progress}
      at={{ question: Math.min(index + 1, exercises.length), of: exercises.length }}
      combo={combo.run}
      hard={combo.hard}
      onLeave={leave}
    >
      <div className="grow" style={{ position: 'relative' }}>
        {current === undefined ? (
          // A lesson the builder emptied — every exercise dropped for this
          // device. Rare, and a dead end rather than a blank screen.
          <div className="pad center">
            <p className="sub">There is nothing to do here yet.</p>
            <button className="btn ghost" type="button" onClick={leave}>
              Back
            </button>
          </div>
        ) : (
          <>
            {/*
              Only while a question is actually being asked. On the feedback
              panel the answer is already given, and there is nothing left to
              report about it that the next question will not offer again.
            */}
            {phase.at === 'asking' && !reporting.thanking && (
              <ReportButton onOpen={reporting.open} />
            )}
            {/*
              Keyed by position, so the same exercise asked again (D-162) is a
              fresh question rather than the old one with its taps still on.
            */}
            <ExerciseView
              key={index}
              exercise={current}
              listener={listener}
              onDone={answered}
            />
          </>
        )}
      </div>

      {reporting.picking && current !== undefined && (
        <ReportSheet
          onClose={reporting.close}
          onPick={(reason) => {
            // Thanked first, and the request sent after — nothing a child sees
            // waits on the network, and nothing they see depends on it landing.
            reporting.thank();
            sendReport(client, {
              exerciseId: current.exerciseId,
              lessonId,
              reason,
            });
          }}
        />
      )}

      {reporting.thanking && (
        <Thanks
          onDone={() => {
            reporting.done();
            reported();
          }}
        />
      )}

      {phase.at === 'feedback' && (
        <Feedback verdict={phase.verdict} again={phase.again} onNext={next} />
      )}
      {phase.at === 'asking' && current === undefined && (
        <div className="pad" style={{ paddingBottom: 24 }}>
          <button
            className="btn"
            type="button"
            onClick={() => {
              void finish();
            }}
          >
            Finish
          </button>
        </div>
      )}
    </Frame>
  );
}

/**
 * What the lesson paid.
 *
 * Straight from the server, which derived it from the answers it stored — the
 * client's own running combo is a flame in the corner and nothing more.
 *
 * A replay shows the same numbers with a quieter frame rather than a row of
 * zeroes: the child did the work again, and "you earned nothing" is not what
 * happened ([D-065](../../../docs/DECISIONS.md#d-065)).
 */
/**
 * Sneakers on the way out, when there is a choice of what he could say.
 *
 * A lesson may have an authored outro, and the child may have just done
 * something worth remarking on. **The child wins.** A milestone, a comeback or
 * a run through hard mode is about *them, today*; the authored line is about
 * the lesson and reads the same on every play. So the responsive line takes the
 * moment when there is one, and the author's line takes every other time —
 * which is most of them, and is where an authored line is worth having.
 *
 * The generic encouragement is the weakest of the three, so an authored outro
 * beats it outright.
 */
function Farewell({
  phase,
  outro,
}: {
  readonly phase: { readonly score: number; readonly earned: Finishing | null };
  readonly outro: Playing['narration']['outro'];
}) {
  const payout = phase.earned?.payout;
  const aboutThisChild =
    payout?.milestone != null || payout?.comeback === true || payout?.hardMode === true;

  // Drawn once: the finish screen re-renders as the recap loads, and a
  // goodbye that changed under a child mid-sentence would be strange.
  const [line] = useState(() => sneakersSays(phase));
  if (outro !== null && !aboutThisChild) {
    return <Sneakers text={outro.text} audioRef={outro.audioRef} />;
  }
  return <Sneakers text={line} audioRef={null} />;
}

/** Sneakers on the way out. Warm either way, and never about a loss. */
function sneakersSays(phase: {
  readonly score: number;
  readonly earned: Finishing | null;
}) {
  const payout = phase.earned?.payout;
  if (payout?.milestone != null) {
    return `${String(payout.milestone)} days in a row! That is really something.`;
  }
  // A comeback is greeted, never counted. "You're back! I missed you." —
  // nothing about the streak that ended (GAMIFICATION.md).
  if (payout?.comeback === true) return 'You are back! I missed you.';
  if (payout?.hardMode === true) return 'You were on fire there! Nicely done.';
  return pick(phase.score >= 0.8 ? FINISHED_WELL : FINISHED_GENTLY);
}

/**
 * Sneakers saying thank you for a report, then getting out of the way.
 *
 * It dismisses itself. There is nothing to decide, so there is nothing to tap:
 * a confirmation button here would make reporting a two-step interaction, and
 * the whole point is that it is one.
 */
function Thanks({ onDone }: { readonly onDone: () => void }) {
  useEffect(() => {
    const wait = setTimeout(onDone, THANKS_MS);
    return () => {
      clearTimeout(wait);
    };
  }, [onDone]);

  return (
    <div className="pad" style={{ paddingBottom: 24 }}>
      <Sneakers text={THANKS} audioRef={null} />
    </div>
  );
}

/**
 * How long the thank-you stays.
 *
 * Long enough to read a short sentence at nine years old, short enough that it
 * never feels like a screen to get past.
 */
const THANKS_MS = 2200;

/** The lesson chrome: a way out, and how far along the child is. */
function Frame({
  progress,
  at,
  combo = 0,
  hard = false,
  onLeave,
  children,
}: {
  readonly progress: number;
  /**
   * Which question this is, and how many there are.
   *
   * A sighted child reads the bar. A child using VoiceOver gets "42 percent",
   * which is not a position — [Accessibility](../../../docs/ACCESSIBILITY.md)
   * asks for "3 of 8" by name. Absent while the lesson is still loading, when
   * there is no position to announce.
   */
  readonly at?: { readonly question: number; readonly of: number };
  /** Right answers in a row, right now. Shown from two, because one is not a run. */
  readonly combo?: number;
  readonly hard?: boolean;
  readonly onLeave: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="frame">
      <div className="top">
        <button
          className="x"
          type="button"
          aria-label="Close the lesson"
          onClick={onLeave}
          style={{ background: 'none', border: 0, font: 'inherit' }}
        >
          ×
        </button>
        <div
          className="bar"
          role="progressbar"
          aria-label={
            at === undefined
              ? 'How far you are'
              : `Question ${String(at.question)} of ${String(at.of)}`
          }
          // The *text* is what a screen reader reads out, and it is the position
          // rather than the percentage. The numeric value stays honest for
          // anything that reads the bar as a bar.
          {...(at === undefined
            ? {}
            : {
                'aria-valuetext': `Question ${String(at.question)} of ${String(at.of)}`,
              })}
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <i
            style={{
              width: `${String(Math.round(progress * 100))}%`,
              ...(hard ? { background: 'var(--flame)' } : {}),
            }}
          />
        </div>
        {combo > 1 && (
          <span
            className={hard ? 'combo' : 'pill'}
            style={
              hard ? undefined : { color: 'var(--flame-ink)', fontSize: '0.8125rem' }
            }
          >
            <span aria-hidden="true">🔥</span> {combo}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * What Sneakers says about the answer.
 *
 * A wrong answer is never called wrong. It gets the warm form and the right
 * answer alongside it, which is the tone the whole app is written in
 * ([Design](../../../docs/DESIGN.md)).
 *
 * Only ever shown for an answer. An exercise this build could show but not ask
 * has nothing to be told about, and goes straight on.
 */
function Feedback({
  verdict,
  again = false,
  onNext,
}: {
  readonly verdict: Extract<Verdict, { kind: 'answered' }>;
  /** Said when this one will come back later in the lesson (D-162). */
  readonly again?: boolean;
  readonly onNext: () => void;
}) {
  const ok = verdict.outcome === 'correct';
  // Drawn once per answer, from hundreds: the same two words after every
  // question stop sounding like anyone is pleased.
  const [headline] = useState(() => pick(ok ? CORRECT : CLOSE));
  return (
    <div className={`fb ${ok ? 'ok' : 'no'}`} role="status">
      <h3 style={{ color: ok ? 'var(--green-ink)' : 'var(--amber-ink)' }}>
        {headline}
      </h3>
      {verdict.correction !== null && (
        <div className="tiny" style={{ fontWeight: 700 }}>
          {/*
            Quoted, because the correction is not always a word. It used to read
            `It is <b>{correction}</b>.` which is fine for `It is please.` and
            nonsense for `It is This is my friend.` — and the sentence-shaped
            corrections arrive the moment a chapter has `word_bank_order` in it,
            which chapters 2 and 3 do. Seen in a screenshot, not in a test.
          */}
          {ok ? (
            // Right, with a spelling slip: the typo rules accepted it
            // (D-149). The spelling is a gift in the green sheet, not a mark.
            <>
              It’s spelled “<b>{verdict.correction}</b>”.
            </>
          ) : (
            <>
              It is “<b>{verdict.correction}</b>”.
            </>
          )}{' '}
          {/* The right answer, out loud, as often as they like. */}
          <button
            className="chip"
            type="button"
            aria-label={`Hear “${verdict.correction}”`}
            onClick={() => {
              void say(null, verdict.correction);
            }}
          >
            <span aria-hidden="true">🔊</span>
          </button>
        </div>
      )}
      {again && (
        <div className="tiny" style={{ fontWeight: 700 }}>
          We will try this one again in a moment.
        </div>
      )}
      <button className="btn" type="button" onClick={onNext}>
        Next
      </button>
    </div>
  );
}
